import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useKandidaatMutaties, useKwalificatieMutaties } from '../../api/kandidaten.ts';
import type { KandidaatProfiel, Opleiding } from '../../api/typen.ts';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { locatieLabel } from '../../componenten/keuzevelden.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { datum, ofStreepje, opsomming, periode, salaris, uren } from '../../helpers/formatteer.ts';
import { vandaag } from '../../helpers/formulier.ts';
import {
  dienstverbandLabels,
  label,
  opleidingsniveauLabels,
  taalnaam,
  taalniveauLabels,
  vaardigheidsniveauLabels,
} from '../../helpers/labels.ts';
import { IndelingFormulier } from './IndelingFormulier.tsx';
import { CertificaatFormulier, OpleidingFormulier, WerkervaringFormulier } from './KwalificatieFormulieren.tsx';
import { TalenFormulier, VaardighedenFormulier } from './VaardighedenTalenFormulieren.tsx';
import { WensenFormulier } from './WensenFormulier.tsx';

type Werkervaring = KandidaatProfiel['werkervaringen'][number];
type Certificaat = KandidaatProfiel['certificaten'][number];
type Kwalificatiesoort = 'werkervaring' | 'opleiding' | 'certificaat';

function Kaart({ titel, actie, children }: { titel: string; actie?: ReactNode | undefined; children: ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={titel} action={actie} slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function BewerkKnop({ onClick, label: tekst = 'Bewerken' }: { onClick: () => void; label?: string }) {
  return (
    <Button size="small" startIcon={<EditOutlinedIcon />} onClick={onClick}>
      {tekst}
    </Button>
  );
}

function RijActies({ onBewerk, onVerwijder }: { onBewerk: () => void; onVerwijder: () => void }) {
  return (
    <Stack direction="row">
      <IconButton size="small" aria-label="Bewerken" onClick={onBewerk}>
        <EditOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="Verwijderen" onClick={onVerwijder}>
        <DeleteOutlinedIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/**
 * Het tabblad Profiel van een kandidaat: wensen, indeling en kwalificaties, elk met hun eigen formulier.
 * @param props Het profiel.
 * @returns Het tabblad.
 */
export function KandidaatProfielTab({ profiel }: { profiel: KandidaatProfiel }) {
  const mutaties = useKandidaatMutaties();
  const kwalificaties = useKwalificatieMutaties(profiel.id);
  const { meld, meldFout } = useMeldingen();
  const [wensen, setWensen] = useState(false);
  const [wensenWissen, setWensenWissen] = useState(false);
  const [indeling, setIndeling] = useState(false);
  const [werkervaring, setWerkervaring] = useState<{ open: boolean; bestaand?: Werkervaring }>({ open: false });
  const [opleiding, setOpleiding] = useState<{ open: boolean; bestaand?: Opleiding }>({ open: false });
  const [certificaat, setCertificaat] = useState<{ open: boolean; bestaand?: Certificaat }>({ open: false });
  const [vaardigheden, setVaardigheden] = useState(false);
  const [talen, setTalen] = useState(false);
  const [verwijderen, setVerwijderen] = useState<{ soort: Kwalificatiesoort; id: string; naam: string } | null>(null);

  const verwijderKwalificatie = () => {
    if (!verwijderen) {
      return;
    }
    const mutatie = kwalificaties[verwijderen.soort].verwijder;
    mutatie
      .mutateAsync(verwijderen.id)
      .then(() => {
        meld('Verwijderd');
        setVerwijderen(null);
      })
      .catch(meldFout);
  };

  const w = profiel.wensen;
  const vandaagIso = vandaag();

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart
            titel="Wensen"
            actie={
              <Stack direction="row">
                <BewerkKnop onClick={() => setWensen(true)} />
                {w && (
                  <IconButton size="small" aria-label="Wensen verwijderen" onClick={() => setWensenWissen(true)}>
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            }
          >
            {w ? (
              <Stack spacing={2}>
                <Kenmerken
                  items={[
                    { label: 'Uren per week', waarde: uren(w.urenMin, w.urenMax) },
                    { label: 'Salaris', waarde: salaris(w.salarisMin, w.salarisMax) },
                    { label: 'Dienstverband', waarde: opsomming(w.dienstverbanden.map((d) => dienstverbandLabels[d])) },
                    { label: 'Maximale reisafstand', waarde: w.maxReisafstandKm ? `${w.maxReisafstandKm} km` : '–' },
                    { label: 'Zoekt vanaf', waarde: datum(w.zoektVanaf) },
                    { label: 'Bijgewerkt', waarde: datum(w.gewijzigdOp) },
                  ]}
                />
                <Kenmerken
                  kolommen={1}
                  items={[
                    {
                      label: 'Gewenste functies',
                      waarde: w.functietitels.length ? (
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          {w.functietitels.map((k) => (
                            <Chip key={k.functietitelId} label={k.functietitel.naam} size="small" />
                          ))}
                        </Stack>
                      ) : null,
                    },
                    {
                      label: 'Gewenste werklocaties',
                      waarde: w.locaties.length ? (
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          {w.locaties.map((k) => (
                            <Chip key={k.locatieId} label={locatieLabel(k.locatie)} size="small" />
                          ))}
                        </Stack>
                      ) : null,
                    },
                  ]}
                />
              </Stack>
            ) : (
              <Typography color="text.secondary">Nog geen wensen vastgelegd.</Typography>
            )}
          </Kaart>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart titel="Indeling" actie={<BewerkKnop onClick={() => setIndeling(true)} />}>
            <Kenmerken
              kolommen={1}
              items={[
                {
                  label: 'Functietitels',
                  waarde: profiel.functietitels.length ? (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {profiel.functietitels.map((k) => (
                        <Chip key={k.functietitelId} label={k.functietitel.naam} size="small" />
                      ))}
                    </Stack>
                  ) : null,
                },
                {
                  label: 'Talentpools',
                  waarde: profiel.talentpools.length ? (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {profiel.talentpools.map((k) => (
                        <Chip
                          key={k.talentpoolId}
                          label={k.talentpool.naam}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  ) : null,
                },
              ]}
            />
          </Kaart>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart
            titel="Werkervaring"
            actie={
              <Button size="small" startIcon={<AddIcon />} onClick={() => setWerkervaring({ open: true })}>
                Toevoegen
              </Button>
            }
          >
            {profiel.werkervaringen.length === 0 ? (
              <Typography color="text.secondary">Geen werkervaring vastgelegd.</Typography>
            ) : (
              <List disablePadding>
                {profiel.werkervaringen.map((item) => (
                  <ListItem
                    key={item.id}
                    disableGutters
                    divider
                    secondaryAction={
                      <RijActies
                        onBewerk={() => setWerkervaring({ open: true, bestaand: item })}
                        onVerwijder={() =>
                          setVerwijderen({ soort: 'werkervaring', id: item.id, naam: item.functietitelLetterlijk })
                        }
                      />
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{item.functietitelLetterlijk}</span>
                          {item.functietitel && <Chip label={item.functietitel.naam} size="small" variant="outlined" />}
                        </Stack>
                      }
                      secondary={
                        <>
                          {item.bedrijf ? (
                            <Link component={RouterLink} to={`/bedrijven/${item.bedrijf.id}`} underline="hover">
                              {item.bedrijf.naam}
                            </Link>
                          ) : (
                            ofStreepje(item.werkgever)
                          )}
                          {' · '}
                          {periode(item.van, item.tot)}
                          {item.omschrijving && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                            >
                              {item.omschrijving}
                            </Typography>
                          )}
                        </>
                      }
                      slotProps={{ secondary: { component: 'div' } }}
                      sx={{ pr: 8 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Kaart>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Kaart
              titel="Opleidingen"
              actie={
                <Button size="small" startIcon={<AddIcon />} onClick={() => setOpleiding({ open: true })}>
                  Toevoegen
                </Button>
              }
            >
              {profiel.opleidingen.length === 0 ? (
                <Typography color="text.secondary">Geen opleidingen vastgelegd.</Typography>
              ) : (
                <List disablePadding>
                  {profiel.opleidingen.map((item) => (
                    <ListItem
                      key={item.id}
                      disableGutters
                      divider
                      secondaryAction={
                        <RijActies
                          onBewerk={() => setOpleiding({ open: true, bestaand: item })}
                          onVerwijder={() =>
                            setVerwijderen({ soort: 'opleiding', id: item.id, naam: item.richting ?? 'opleiding' })
                          }
                        />
                      }
                    >
                      <ListItemText
                        primary={
                          [label(opleidingsniveauLabels, item.niveau), item.richting].filter(Boolean).join(' · ') ||
                          'Opleiding'
                        }
                        secondary={[
                          item.instelling,
                          item.eindjaar,
                          item.diplomaBehaald === true
                            ? 'diploma behaald'
                            : item.diplomaBehaald === false
                              ? 'geen diploma'
                              : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        sx={{ pr: 8 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Kaart>
            <Kaart
              titel="Certificaten"
              actie={
                <Button size="small" startIcon={<AddIcon />} onClick={() => setCertificaat({ open: true })}>
                  Toevoegen
                </Button>
              }
            >
              {profiel.certificaten.length === 0 ? (
                <Typography color="text.secondary">Geen certificaten vastgelegd.</Typography>
              ) : (
                <List disablePadding>
                  {profiel.certificaten.map((item) => {
                    const verlopen = item.geldigTot !== null && item.geldigTot < vandaagIso;
                    return (
                      <ListItem
                        key={item.id}
                        disableGutters
                        divider
                        secondaryAction={
                          <RijActies
                            onBewerk={() => setCertificaat({ open: true, bestaand: item })}
                            onVerwijder={() =>
                              setVerwijderen({ soort: 'certificaat', id: item.id, naam: item.certificaattype.naam })
                            }
                          />
                        }
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                              <span>{item.certificaattype.naam}</span>
                              {verlopen && <Chip label="Verlopen" size="small" color="error" variant="outlined" />}
                            </Stack>
                          }
                          secondary={`Behaald ${datum(item.behaaldOp)} · geldig tot ${datum(item.geldigTot)}`}
                          sx={{ pr: 8 }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Kaart>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart titel="Vaardigheden" actie={<BewerkKnop onClick={() => setVaardigheden(true)} />}>
            {profiel.vaardigheden.length === 0 ? (
              <Typography color="text.secondary">Geen vaardigheden vastgelegd.</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {profiel.vaardigheden.map((item) => (
                  <Chip
                    key={item.vaardigheidId}
                    label={
                      item.niveau
                        ? `${item.vaardigheid.naam} · ${vaardigheidsniveauLabels[item.niveau]}`
                        : item.vaardigheid.naam
                    }
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Kaart>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart titel="Talen" actie={<BewerkKnop onClick={() => setTalen(true)} />}>
            {profiel.talen.length === 0 ? (
              <Typography color="text.secondary">Geen talen vastgelegd.</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {profiel.talen.map((item) => (
                  <Chip
                    key={item.taalcode}
                    label={
                      item.niveau
                        ? `${taalnaam(item.taalcode)} · ${taalniveauLabels[item.niveau]}`
                        : taalnaam(item.taalcode)
                    }
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Kaart>
        </Grid>
      </Grid>

      <WensenFormulier
        open={wensen}
        onSluit={() => setWensen(false)}
        kandidaatId={profiel.id}
        bestaand={profiel.wensen}
      />
      <IndelingFormulier
        open={indeling}
        onSluit={() => setIndeling(false)}
        functietitels={profiel.functietitels.map((k) => k.functietitel)}
        talentpools={profiel.talentpools.map((k) => k.talentpool)}
        opslaan={(invoer) => mutaties.zetIndeling.mutateAsync({ id: profiel.id, invoer })}
      />
      <WerkervaringFormulier
        open={werkervaring.open}
        onSluit={() => setWerkervaring({ open: false })}
        kandidaatId={profiel.id}
        cvs={profiel.cvs}
        bestaand={werkervaring.bestaand}
      />
      <OpleidingFormulier
        open={opleiding.open}
        onSluit={() => setOpleiding({ open: false })}
        kandidaatId={profiel.id}
        cvs={profiel.cvs}
        bestaand={opleiding.bestaand}
      />
      <CertificaatFormulier
        open={certificaat.open}
        onSluit={() => setCertificaat({ open: false })}
        kandidaatId={profiel.id}
        cvs={profiel.cvs}
        bestaand={certificaat.bestaand}
      />
      <VaardighedenFormulier
        open={vaardigheden}
        onSluit={() => setVaardigheden(false)}
        kandidaatId={profiel.id}
        bestaand={profiel.vaardigheden}
      />
      <TalenFormulier open={talen} onSluit={() => setTalen(false)} kandidaatId={profiel.id} bestaand={profiel.talen} />
      <BevestigDialoog
        open={wensenWissen}
        titel="Wensen verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijderWensen.isPending}
        onSluit={() => setWensenWissen(false)}
        onBevestig={() =>
          mutaties.verwijderWensen
            .mutateAsync({ id: profiel.id })
            .then(() => {
              meld('Wensen verwijderd');
              setWensenWissen(false);
            })
            .catch(meldFout)
        }
      >
        Alle wensen van deze kandidaat worden verwijderd.
      </BevestigDialoog>
      <BevestigDialoog
        open={verwijderen !== null}
        titel="Verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={verwijderen ? kwalificaties[verwijderen.soort].verwijder.isPending : false}
        onSluit={() => setVerwijderen(null)}
        onBevestig={verwijderKwalificatie}
      >
        Weet je zeker dat je {verwijderen?.naam} wilt verwijderen?
      </BevestigDialoog>
    </>
  );
}
